import * as React from 'react';

import type {
    GetQuoteById_quote_productLineItems_edges_node as ProductLineItem,
    QuoteProductsUpdateInput,
    GetPossibleProducts_quotePossibleProducts as QuotePossibleProducts,
    GetPossibleProducts_quotePossibleProducts_edges as QuotePossibleProductsEdge,
    GetQuoteById_quote_productLineItems as ProductLineItems,
    GetQuoteById_quote_total as Money,
} from 'nutshell-graphql-types';

import type {SalesDocumentType} from '../../lead-sidebar/sales-documents/types';
import {SaveableWrapper} from '../../saveable-wrapper/saveable-wrapper';

import {
    PRODUCT_TABLE,
    PRODUCT_TABLE_TEMPLATE,
    generateProductLineItemInputType,
    makeDeepProductLineItemCopy,
} from '../helpers';
import {QuoteProductTable} from '../quotes-product-table/quote-product-table';

type Props = {
    type: SalesDocumentType;
    onSave: (arg1: QuoteProductsUpdateInput) => void;
    onCancel: () => void;
    isActive: boolean;
    id: string;
    isSkuShown: boolean;
    isTermShown: boolean;
    isDiscountShown: boolean;
    productLineItems: ProductLineItems;
    total: Money;
    onToggleActive: (arg1: string) => void;
    quotePossibleProductsEdges: QuotePossibleProducts;
    isTemplate: boolean;
    forceSave: boolean;
    isSmallScreen: boolean;
    isImmutable: boolean;
};

export function QuoteProductTableWrapper(props: Props) {
    const {id, onSave} = props;

    const [showSku, setShowSku] = React.useState<boolean>(props.isSkuShown);
    const [showTerm, setShowTerm] = React.useState<boolean>(props.isTermShown);
    const [showDiscount, setShowDiscount] = React.useState<boolean>(props.isDiscountShown);
    const [products, setProducts] = React.useState<ProductLineItem[]>(
        props.productLineItems.edges.map((edge) => {
            return makeDeepProductLineItemCopy(edge.node);
        })
    );
    const [possibleProducts, setPossibleProducts] = React.useState<
        Array<QuotePossibleProductsEdge>
    >([]);

    React.useEffect(() => {
        if (props.quotePossibleProductsEdges && props.quotePossibleProductsEdges.edges) {
            setPossibleProducts(props.quotePossibleProductsEdges.edges.map((edge) => edge));
        }
    }, [props.quotePossibleProductsEdges]);

    React.useEffect(() => {
        if (!props.isActive) {
            setProducts(
                props.productLineItems.edges.map((edge) => {
                    return makeDeepProductLineItemCopy(edge.node);
                })
            );

            setShowSku(props.isSkuShown);
            setShowTerm(props.isTermShown);
            setShowDiscount(props.isDiscountShown);
        }
    }, [
        props.productLineItems,
        props.isSkuShown,
        props.isTermShown,
        props.isDiscountShown,
        props.isActive,
    ]);

    const handleSave = React.useCallback(() => {
        const productLineItemsInput = products.map((product) =>
            generateProductLineItemInputType(product)
        );

        onSave({
            id,
            products: productLineItemsInput,
            isSkuShown: showSku,
            isTermShown: showTerm,
            isDiscountShown: showDiscount,
        });
    }, [id, onSave, products, showSku, showTerm, showDiscount]);

    React.useEffect(() => {
        if (props.forceSave) {
            handleSave();
        }
    }, [props.forceSave, handleSave]);

    const handleCancel = () => {
        setShowSku(props.isSkuShown);
        setShowTerm(props.isTermShown);
        setShowDiscount(props.isDiscountShown);
        setProducts(
            props.productLineItems.edges.map((edge) => {
                return makeDeepProductLineItemCopy(edge.node);
            })
        );
        props.onCancel();
    };

    return (
        <SaveableWrapper
            isActive={props.isActive}
            onSave={handleSave}
            onCancel={handleCancel}
            onToggleActive={props.onToggleActive}
            sectionName={props.isTemplate ? PRODUCT_TABLE_TEMPLATE : PRODUCT_TABLE}
            noPadding={true}
            overflowVisible={true}
            isImmutable={props.isImmutable}
            showEditIcon={props.isTemplate}
            centerContent={true}
        >
            <QuoteProductTable
                isLoading={false}
                errorMessage={null}
                isEditable={props.isActive}
                showSku={showSku}
                showTerm={showTerm}
                showDiscount={showDiscount}
                products={products}
                setProducts={setProducts}
                setShowSku={setShowSku}
                setShowTerm={setShowTerm}
                setShowDiscount={setShowDiscount}
                quotePossibleProductsEdges={possibleProducts}
                isTemplate={props.isTemplate}
                quoteTotal={props.total}
                isSmallScreen={props.isSmallScreen}
                type={props.type}
            />
        </SaveableWrapper>
    );
}
